import L from 'leaflet';
import { store } from 'src/store';
import {
  hideAll,
  hideGeomLayer,
  hideGeomLayers,
  showGeomLayer,
} from './gisReducer';
import { featureCollection } from '@turf/helpers';

const createFeatureStyle = (feature) => {
  const style = feature.properties.style;
  const color = style?.color || 'blue';
  const opacity = style?.opacity || style?.fillOpacity || 1;
  const strokeWidth = style.weight || 3;
  let dashArray = 'none';
  if (style.dashArray && style.dashArray !== 'solid') {
    const isDashed = style.dashArray === 'dashed';
    const dashSize = isDashed ? strokeWidth * 3 : strokeWidth;
    const gapSize = isDashed ? strokeWidth * 2 : strokeWidth * 2.5;
    dashArray = `${dashSize},${gapSize}`;
  }
  return {
    color: color,
    weight: style?.weight || 2,
    opacity: opacity,
    radius: style?.radius || 5,
    fillColor: color, // fill with the selected color
    fillOpacity: opacity, // Re use opacity for fillOpacity
    dashArray: dashArray,
  };
};

const createPointMarker = (feature, latlng) => {
  const style = feature.properties.style;
  const shape = style?.markerShape || 'pin';
  const radius = parseFloat(style?.radius) || 5;
  const fillColor = style?.color || 'blue';
  const fillOpacity = style?.opacity || 1;
  if (shape !== 'circle') {
    let extraProps = {};
    let iconSize = [radius * 2, radius * 2];
    if (shape === 'pin') {
      iconSize = [radius * 1.4, radius * 2];
      extraProps['iconAnchor'] = [radius * 0.7, radius * 2];
    }

    return new L.marker(latlng, {
      icon: L.divIcon({
        html: getShapeHTML(shape, radius, fillColor, fillOpacity),
        iconSize,
        className: 'custom-shape-marker',
        ...extraProps,
      }),
    });
  }

  return new L.circleMarker(latlng, {
    fillColor: style?.color || 'blue',
    fillOpacity: style?.fillOpacity || 1,
    radius: style?.radius || 5,
  }); // Default fallback
};

let PIN_CACHE = {};

const createPinPath = (sizePx) => {
  // this is the "unit" pin path for a 0.7px wide by 1 px high shape
  // it needs to be scaled to give the appropriate pin size in px.
  if (!(sizePx in PIN_CACHE)) {
    const templatePin =
      'M 0.35,0 C 0.1565,0 0,0.1565 0,0.35 0,0.6125 0.35,1 0.35,1 0.35,1 0.7,0.6125 0.7,0.35 0.7,0.1565 0.5435,0 0.35,0 Z';

    const scale = (v) => {
      return v * sizePx;
    };

    const splitPath = templatePin.split(' ');
    const scaledPin = splitPath
      .map((v) => {
        if (v.includes(',')) {
          const [x, y] = v.split(',');
          return [scale(parseFloat(x), 0), scale(parseFloat(y), -0.5)].join(
            ','
          );
        } else {
          return v;
        }
      })
      .join(' ');
    PIN_CACHE[sizePx] = scaledPin;
  }

  return PIN_CACHE[sizePx];
};

const getStyle = (width, height, color, opacity) => {
  return `width:${width}px;height:${height}px;color:${color};opacity:${opacity}`;
};

const getShapeHTML = (shape, radius, color, opacity) => {
  if (shape === 'pin') {
    const style = getStyle(radius * 1.4, radius * 2, color, opacity);
    return `
    <svg style="${style}">
        <path d="${createPinPath(radius * 2)}" fill="${color}" />
    </svg>
    `;
  }

  const style = getStyle(radius * 2, radius * 2, color, opacity);
  switch (shape) {
    case 'square':
      return `<div style="${style};background:${color}"></div>`;
    case 'triangle':
      return `
                <svg style="${style}" viewBox="0 0 100 100">
                    <polygon points="50,0 0,100 100,100" fill="${color}"/>
                </svg>
            `;
    default:
      return `<div style="${style};border-radius:50%;background:${color}"></div>`;
  }
};

export default class GisLayerUtils {
  #canvas = null;
  #geomLayers = {};

  constructor(canvas) {
    this.#canvas = canvas;
  }

  showLayer(layer) {
    if (!layer?.features || layer?.features.length < 1) {
      return;
    }

    const firstFeature = layer.features[0];
    const props = firstFeature.properties;
    const layerId = props.layer_id;

    if (firstFeature.type == 'overlay') {
      this.showOverlays(layer, layerId);
    } else {
      this.showGeoJSON(layer, layerId);
    }

    store.dispatch(
      showGeomLayer({
        layerId,
      })
    );
  }

  fitLayer(layerId) {
    const layers = this.#geomLayers[layerId];
    if (layers) {
      let bounds = null;
      for (const layer of layers) {
        let layerBounds = layer.getBounds();
        if (!layerBounds) {
          // assume distortableImageOverlay
          // console.log(layer.getCenter());
          const corners = layer.getCorners();
          layerBounds = L.latLngBounds([corners[0], corners[3]]);
        }
        if (!bounds) {
          bounds = layerBounds;
        } else if (layerBounds) {
          bounds.extend(layerBounds);
        }
      }
      try {
        this.#canvas.map.fitBounds(bounds);
      } catch {
        console.warn('Cannot fit bounds', layer, bounds);
      }
    }
  }

  showOverlay(feature, layerId) {
    const props = feature.properties;
    const imageUrl = props.url;
    const imageBounds = props.bounds;
    const bounds = {
      nw_lat: imageBounds.north,
      nw_lng: imageBounds.west,
      se_lat: imageBounds.south,
      se_lng: imageBounds.east,
    };
    bounds.ne_lat = bounds.nw_lat;
    bounds.ne_lng = bounds.se_lng;
    bounds.sw_lat = bounds.se_lat;
    bounds.sw_lng = bounds.nw_lng;

    const newLayer = L.distortableImageOverlay(imageUrl, {
      tooltipText: props.name,
      mode: 'lock',
      suppressToolbar: true,
      corners: [
        L.latLng(bounds.nw_lat, bounds.nw_lng),
        L.latLng(bounds.ne_lat, bounds.ne_lng),
        L.latLng(bounds.sw_lat, bounds.sw_lng),
        L.latLng(bounds.se_lat, bounds.se_lng),
      ],
    });
    // hack to stop errors with missing toolbar
    newLayer.editing = {};

    if (!this.#geomLayers[layerId]) {
      this.#geomLayers[layerId] = [];
    }
    this.#geomLayers[layerId].push(newLayer);
    this.#canvas.map.addLayer(newLayer);

    // apply overlay map scaling after adding the layer to the map
    // newLayer.scaleBy(0.75);
    newLayer.rotateBy(-imageBounds.rotation, 'deg');
    newLayer.setOpacity(props.style.opacity || 0.4);
  }

  showOverlays(layer, layerId) {
    this.hideGeomLayer(layerId);
    for (let feature of layer.features) {
      this.showOverlay(feature, layerId);
    }
  }

  showGeoJSON(layer, layerId) {
    // Remove existing layer if any for avoiding duplicates.
    this.hideGeomLayer(layerId);

    const newLayer = new L.geoJSON(layer, {
      style: createFeatureStyle,
      pointToLayer: createPointMarker,
      onEachFeature: function (feature, layer) {
        const name = feature.properties.name;
        const tootlTipText = `${name}`;
        layer.bindTooltip(tootlTipText, {
          direction: 'top',
          offset: [0, -10],
          permanent: false,
          opacity: 0.9,
        });
      },
    });
    this.#geomLayers[layerId] = [newLayer];
    this.#canvas.map.addLayer(newLayer);
  }

  fitLayers(geomLayers) {
    // zoom to fit a list of geojson geom layers
    const fc = featureCollection(geomLayers);
    try {
      this.#canvas.map.fitBounds(L.geoJson(fc).getBounds());
    } catch {
      console.error('Unable to fit bounds for', geomLayers);
    }
  }

  hideGeomLayer(layerId, withDispatch = true) {
    const layers = this.#geomLayers[layerId];
    if (layers) {
      for (const layer of layers) {
        // hack to stop errors with DistortableImageOverlay
        layer.editing = { disable: () => {} };
        this.#canvas.map.removeLayer(layer);
      }
    }
    delete this.#geomLayers[layerId];
    if (withDispatch) {
      store.dispatch(
        hideGeomLayer({
          layerId,
        })
      );
    }
  }

  hideLayers(layerIds) {
    if (!layerIds) {
      return;
    }

    layerIds.forEach((layerId) => {
      this.hideGeomLayer(layerId, false);
    });
    store.dispatch(hideGeomLayers({ layerIds }));
  }

  hideAllGeomLayers(dispatch = true) {
    const layerIds = Object.keys(this.#geomLayers);
    layerIds.forEach((layerId) => {
      this.hideGeomLayer(layerId, false);
    });
    this.#geomLayers = {};
    if (dispatch) {
      store.dispatch(hideAll({}));
    }
  }
}
